import { useEffect, useState } from 'react'
import type { ClusterSummary } from '@/db/api'
import { loadWords } from '@/lib/agentGraph'
import { podWords } from '@/lib/podWords'
import { healthDot, type Health } from '@/lib/colour'
import { Tag, cx } from '@/components/ui'

/**
 * Pieces the online pages share (Releases, Infrastructure, Settings → Publish,
 * the Graph's detail panel): an online-only read that refreshes while the page
 * shows, the dot that says whether a thing is up, and the words for a node, a
 * pod that needs a look and an Argo CD app.
 */

/** Loads `load` now and every `every` ms while the page is visible; keeps the last answer through a failure. */
export const useOnline = <T,>(load: () => Promise<T>, every: number) => {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState('')
  const [tick, setTick] = useState(0)
  useEffect(() => {
    let stale = false
    const run = () =>
      load()
        .then((d) => {
          if (!stale) {
            setData(d)
            setError('')
          }
        })
        .catch((e: Error) => !stale && setError(navigator.onLine ? e.message : 'Offline.'))
    void run()
    const t = window.setInterval(() => document.visibilityState === 'visible' && void run(), every)
    return () => {
      stale = true
      window.clearInterval(t)
    }
    // load is a module-level call; tick reloads on demand
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, every])
  return { data, error, reload: () => setTick((n) => n + 1) }
}

/** A small dot: up, needs a look, or neither (finished, unknown). */
export const HealthDot = ({ health }: { health: Health }) => (
  <span className={cx('h-2 w-2 rounded-full shrink-0', healthDot[health])} aria-hidden />
)

type ClusterNode = ClusterSummary['nodes'][number]
type ClusterPod = ClusterSummary['problems'][number]
type ClusterApp = ClusterSummary['apps'][number]

/** A cluster node: its load, pods and what it is for. */
export const HostDetail = ({ node }: { node: ClusterNode }) => (
  <>
    <div className="flex flex-wrap items-center gap-1.5">
      <Tag tone={node.ready ? undefined : 'warn'}>{node.ready ? 'Ready' : 'Not ready'}</Tag>
      {node.home && <Tag tone="accent">Home node</Tag>}
      {node.gvisor && <Tag>Takes sandboxes</Tag>}
    </div>
    <p className="text-[13px]">
      {loadWords(node.cpu)} cpu, {loadWords(node.memory)} memory, {node.pods} pods
    </p>
    <p className="text-[12px] text-faint">
      {[
        node.arch,
        node.region === 'home' ? 'at home' : node.region,
        node.roles?.replace(/,/g, ', '),
        node.version,
        node.pressure ? `pressure: ${node.pressure}` : '',
      ]
        .filter(Boolean)
        .join(', ')}
    </p>
  </>
)

/** A pod that needs a look: its phase, why, and where. */
export const ProblemDetail = ({ pod }: { pod: ClusterPod }) => (
  <>
    <div className="flex flex-wrap items-center gap-1.5">
      <Tag tone="warn">{pod.phase}</Tag>
      {pod.reason && <Tag>{podWords(pod.reason)}</Tag>}
      {pod.restarts ? <Tag>{pod.restarts} restarts</Tag> : null}
    </div>
    <p className="text-[13px] text-muted break-words">
      {pod.namespace}/{pod.name}
      {pod.node ? ` on ${pod.node}` : ''}
    </p>
  </>
)

/** An Argo CD app's health and sync, in Argo CD's own words. */
export const AppTags = ({ app }: { app: ClusterApp }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    <Tag tone={app.health === 'Healthy' ? undefined : 'warn'}>{app.health || 'Unknown'}</Tag>
    <Tag>{app.sync || 'Unknown'}</Tag>
  </div>
)
